import { useMemo, useState } from 'react';
import {
  IconAddressBook, IconAlarm, IconAlertTriangle, IconCalendarPlus, IconCheck, IconChevronLeft, IconClipboardCheck, IconClockHour4, IconDeviceFloppy,
  IconExternalLink, IconFileText, IconInbox, IconLock, IconPhone, IconRefresh, IconSparkles,
} from '@tabler/icons-react';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { useT } from '@hana/i18n';
import { Button, Modal, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import {
  KIND, abbr, actIcon, actType, dueIcon, dueShort, groupByCustomer, inScope, pace, taskKey, visibleTasks, TASKS,
  type Scope, type SaleTask,
} from './salesTodayData';
import s from './salesTodayCompact.module.css';

// Mint-SalesToday-iPad (danh sách khách trái · việc của khách phải · sổ tay) · Mint-SalesToday-Phone (danh sách → việc một khách + bảng trượt ghi kết quả).
// Chỉ đọc: Gọi / Mở / Ghi kết quả báo toast. Không hiện tiền. Dữ liệu mẫu từ salesToday.ts (script board).

const OUTS = ['Đã nói chuyện', 'Không bắt máy', 'Hẹn lại'] as const;
const NEXTS = ['Không hẹn', '+1 ngày LV', '+2 ngày LV'] as const;
const NOTES: readonly string[] = ['Gửi lịch tàu YML tuần 44 cho GREENBUD', 'Hỏi ANDG về giá RF Jeddah'];
const TONE: Record<string, string | undefined> = { pri: s.tPri, warn: s.tWarn, err: s.tErr, ok: s.tOk, none: '' };

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

function DueIc({ due }: { due: string }) {
  const k = dueIcon(due);
  if (k === 'alarm') return <IconAlarm size={14} stroke={1.8} aria-hidden="true" />;
  if (k === 'clock') return <IconClockHour4 size={14} stroke={1.8} aria-hidden="true" />;
  if (k === 'warn') return <IconAlertTriangle size={14} stroke={1.8} aria-hidden="true" />;
  return null;
}

function ActIc({ k }: { k: ReturnType<typeof actIcon> }) {
  const p = { size: 16, stroke: 1.8, 'aria-hidden': true as const };
  if (k === 'open' || k === 'req') return <IconExternalLink {...p} />;
  if (k === 'refresh') return <IconRefresh {...p} />;
  if (k === 'renew') return <IconCalendarPlus {...p} />;
  if (k === 'contract') return <IconFileText {...p} />;
  if (k === 'call') return <IconPhone {...p} />;
  return null;
}

export function SalesTodayCompact({ phone }: { phone: boolean }) {
  const t = useT();
  const toast = useToast();
  const forced = readState();
  const state: ScreenState = forced ?? 'ready';
  const noperm = state === 'noperm';
  const ready = state === 'ready' || noperm;
  const empty = state === 'empty';
  const [scopeSel, setScopeSel] = useState<Scope>('mine');
  const [sel, setSel] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState<Record<number, boolean>>({});
  const [sheet, setSheet] = useState(false);
  const [out, setOut] = useState<string>(OUTS[0]);
  const [nx, setNx] = useState<string>(NEXTS[2]);
  const scope: Scope = noperm ? 'mine' : scopeSel;

  const rows = useMemo(() => (empty ? [] : inScope(visibleTasks(TASKS, '30'), scope, null)), [empty, scope]);
  const groups = useMemo(() => groupByCustomer(rows, done, phone), [rows, done, phone]);
  const cur = groups.find((g) => g.sn === sel) ?? groups[0];
  const p = pace(rows, done);
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const callCur = () => cur && toast({ msg: `Đang gọi ${cur.contact}` });
  const act = (x: SaleTask) => toast({ msg: `Mở ${x.act.toLowerCase()} chưa mở ở bản này` });
  const left = rows.filter((x) => !done[taskKey(x)]).length;
  const flip = (x: SaleTask) => setDone({ ...done, [taskKey(x)]: !done[taskKey(x)] });

  const scopes = (
    <Segmented<Scope>
      label={t('crm.at.81')}
      value={scope}
      onChange={(k) => { setScopeSel(k); setSel(null); }}
      items={noperm ? [{ key: 'mine', label: 'Của tôi' }] : [{ key: 'mine', label: 'Của tôi' }, { key: 'team', label: 'Cả nhóm' }]}
    />
  );

  const paceCard = rows.length > 0 && (
    <section aria-label={t('crm.at.92')} className={s.pace}>
      <div className={s.between}>
        <b>{t('crm.st.byDue')}</b>
        <span>
          <b className={s.lab}>{p.pct}%</b> <span className={s.lb}>đã xong {p.done}/{p.all} việc</span>
        </span>
      </div>
      <div className={s.bar} role="img" aria-label={`Việc theo hạn: ${p.segs.map((x) => `${x.k} ${x.n}`).join(', ')}`}>
        {p.segs.filter((x) => x.n > 0).map((x) => (
          <div key={x.k} style={{ flex: `${x.n} 1 0`, background: x.c }} />
        ))}
      </div>
      <div className={s.lg}>
        {p.segs.map((x) => (
          <span key={x.k}>
            <i style={{ background: x.c }} />
            {x.k} <b>{x.n}</b>
          </span>
        ))}
      </div>
      <div className={s.ticks} role="img" aria-label={`Đã xong ${p.done} trên ${p.all} việc`}>
        {Array.from({ length: 20 }, (_, i) => (
          <div key={i} className={i < Math.round((p.pct / 100) * 20) ? s.on : undefined} />
        ))}
      </div>
    </section>
  );

  const list = (
    <div className={phone ? s.list : s.listPad}>
      {groups.map((g) => (
        <button key={g.sn} type="button" className={`${s.cust} ${g.sn === cur?.sn && !phone ? s.custOn : ''}`} onClick={() => { setSel(g.sn); setOpen(true); }}>
          <span className={s.ini}>{g.sn.slice(0, 2)}</span>
          <span className={s.cm}>
            <b>{g.sn}</b>
            <span>{g.rows[0]?.text}</span>
          </span>
          <span className={`${s.cnt} ${g.overdue ? s.hot : ''}`} title={g.overdue ? `${g.overdue} quá hạn` : `${g.open} việc`}>
            {g.overdue ? <IconAlertTriangle size={14} stroke={1.8} aria-hidden="true" /> : <IconClipboardCheck size={14} stroke={1.8} aria-hidden="true" />}
            {g.overdue || g.open}
          </span>
        </button>
      ))}
    </div>
  );

  const items = cur && (
    <>
      {cur.rows.map((r) => {
        const k = taskKey(r);
        const isDone = !!done[k];
        const at = actType(r);
        const go = at === 'call' ? () => toast({ msg: `Đang gọi ${cur.contact}` }) : at === 'ai' ? noWrite : () => act(r);
        return (
          <div key={k} className={`${s.item} ${phone ? s.itemPh : ''} ${isDone ? s.itemDone : ''}`}>
            {!phone && (
              <button type="button" className={`${s.cb} ${isDone ? s.cbOn : ''}`} aria-pressed={isDone} aria-label={t('crm.at.93')} onClick={() => flip(r)}>
                {isDone && <IconCheck size={20} stroke={2.4} aria-hidden="true" />}
              </button>
            )}
            <div className={s.im}>
              <div className={s.l1}>
                <span className={`${s.chip} ${TONE[KIND[r.kind].tone]}`}>{KIND[r.kind].label}</span>
                {phone && (
                  <span className={`${s.due} ${r.lvl < 0 ? s.over : r.lvl === 0 ? s.today : ''}`} title={r.due} style={{ marginLeft: 'auto' }}>
                    <DueIc due={r.due} />
                    {dueShort(r.due)}
                  </span>
                )}
              </div>
              <div className={s.tx}>{r.text}</div>
              {!phone && (
                <div className={`${s.due} ${r.lvl < 0 ? s.over : r.lvl === 0 ? s.today : ''}`} title={r.due}>
                  <DueIc due={r.due} />
                  {dueShort(r.due)}
                </div>
              )}
              {phone && (
                <div className={s.acts}>
                  <button type="button" className={s.big} onClick={() => flip(r)}>
                    {!isDone && <IconCheck size={16} stroke={1.8} aria-hidden="true" />}
                    {isDone ? 'Bỏ đánh dấu' : 'Xong'}
                  </button>
                  <button type="button" className={`${s.big} ${s.bigPri}`} onClick={go}>
                    {at === 'ai' ? <IconSparkles size={16} stroke={1.8} aria-hidden="true" /> : <ActIc k={actIcon(r.act)} />}
                    {r.act}
                  </button>
                </div>
              )}
            </div>
            {!phone && (
              <button type="button" className={s.go} onClick={go}>
                {at === 'ai' ? <IconSparkles size={16} stroke={1.8} aria-hidden="true" /> : <ActIc k={actIcon(r.act)} />}
                {r.act}
              </button>
            )}
          </div>
        );
      })}
    </>
  );

  const note = noperm && (
    <div role="note" className={s.note}>
      <IconLock size={16} aria-hidden="true" /> {t('crm.x.teamNeedSeat')}
    </div>
  );
  const view = (inner: React.ReactNode) => (
    <StateView
      state={ready && !empty ? 'ready' : state}
      title={empty ? 'Hôm nay chưa có việc' : state === 'error' ? 'Không tải được việc hôm nay' : undefined}
      desc={empty ? 'Hôm nay chưa có việc.' : state === 'error' ? 'Mất kết nối tới máy chủ. Thử lại sau ít phút.' : undefined}
      icon={empty ? <IconInbox size={26} stroke={1.8} /> : undefined}
      action={state === 'error' ? <Button onClick={noWrite}>{t('crm.retry')}</Button> : undefined}
      skeleton={<Skeleton rows={6} />}
    >
      {inner}
    </StateView>
  );

  /* ---- Điện thoại · khung 2: việc của một khách ---- */
  if (phone && open && cur && ready && !empty) {
    return (
      <Page>
        <PageHeader
          title={cur.sn}
          sub={
            <span className={s.sub}>
              <IconAddressBook size={14} stroke={1.8} aria-hidden="true" />
              {cur.contact}
            </span>
          }
          lead={
            <button type="button" className={s.back} aria-label={t('crm.at.94')} onClick={() => setOpen(false)}>
              <IconChevronLeft size={22} aria-hidden="true" />
            </button>
          }
          actions={<HeaderTool label={`Gọi ${cur.sn}`} icon={IconPhone} onClick={callCur} />}
        />
        <div className={s.detail}>{items}</div>
        <div className={s.dock}>
          <button type="button" className={`${s.big} ${s.bigInk}`} onClick={() => setSheet(true)}>
            <IconClipboardCheck size={16} stroke={1.8} aria-hidden="true" />
            {t('crm.x.logFollow')}
          </button>
        </div>
        <Modal
          open={sheet}
          onOpenChange={setSheet}
          title={`Kết quả với ${cur.sn}`}
          footer={
            <Button variant="primary" icon={<IconDeviceFloppy size={16} aria-hidden="true" />} onClick={() => { setSheet(false); noWrite(); }}>
              {t('crm.x.save')}
            </Button>
          }
        >
          <div className={s.sheet}>
            <div role="radiogroup" aria-label={t('crm.at.95')} className={s.seg3}>
              {OUTS.map((k) => (
                <button key={k} type="button" role="radio" aria-checked={out === k} onClick={() => setOut(k)}>{k}</button>
              ))}
            </div>
            <textarea aria-label={t('crm.at.96')} rows={3} placeholder={t('crm.at.97')} className={s.ta} />
            <div role="radiogroup" aria-label={t('crm.at.98')} className={s.seg3}>
              {NEXTS.map((k) => (
                <button key={k} type="button" role="radio" aria-checked={nx === k} onClick={() => setNx(k)}>{k}</button>
              ))}
            </div>
          </div>
        </Modal>
      </Page>
    );
  }

  /* ---- Khung 1 ---- */
  return (
    <Page kind="table">
      <PageHeader
        title={t('crm.at.99')}
        sub={`Thứ Năm 22/Oct/2026${phone ? ` · ${left} việc` : ''}`}
        tabs={scopes}
        lead={
          phone ? (
            <a className={s.back} href="/more" aria-label={t('crm.at.5')}>
              <IconChevronLeft size={22} aria-hidden="true" />
            </a>
          ) : undefined
        }
      />
      {view(
        phone ? (
          <div className={s.page}>
            {note}
            {paceCard}
            {list}
          </div>
        ) : (
          <div className={s.pad}>
            <section aria-label={t('crm.at.100')} className={s.left}>
              {note}
              {paceCard}
              {list}
            </section>
            <section aria-label={t('crm.at.101')} className={s.right}>
              {cur && (
                <>
                  <div className={s.rh}>
                    <div className={s.rn}>
                      <b>{cur.sn}</b>
                      <div className={s.meta}>
                        <span title={`Liên hệ ${cur.contact}`}>
                          <IconAddressBook size={15} stroke={1.8} aria-hidden="true" />
                          {cur.contact}
                        </span>
                        <span title={`Sale ${cur.sale}`}>
                          <span className={s.ab} aria-hidden="true">{abbr(cur.sale)}</span>
                          {cur.sale}
                        </span>
                      </div>
                    </div>
                    <button type="button" className={s.call} onClick={callCur}>
                      <IconPhone size={16} stroke={1.9} aria-hidden="true" />
                      {t('crm.x.call')}
                    </button>
                  </div>
                  {items}
                  <div className={s.notes}>
                    <b>{t('crm.stc.notes')}</b>
                    {NOTES.map((k, i) => (
                      <button key={k} type="button" className={`${s.nt} ${notes[i] ? s.ntDone : ''}`} onClick={() => setNotes({ ...notes, [i]: !notes[i] })}>
                        {notes[i] ? '☑' : '☐'} {k}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </section>
          </div>
        ),
      )}
    </Page>
  );
}
